<script setup lang="ts">
import { computed, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import AppDeltaPill from '../AppDeltaPill/AppDeltaPill.vue'
import AppMeter from '../AppMeter/AppMeter.vue'
import AppSparkline from '../../charts/AppSparkline/AppSparkline.vue'
import { useCountUp } from '../../../composables/useCountUp'
import { useFormatters } from '../../../composables/useFormatters'
import type { MetricCellProps } from './app-metric-cell-types'

const props = withDefaults(
  defineProps<
    MetricCellProps & {
      /** Leaves the label line out, for a cell whose caption is drawn elsewhere, such as a tile's rim; the label still names the meter. */
      hideLabel?: boolean
    }
  >(),
  {
    displayOverride: undefined,
    total: undefined,
    suffix: undefined,
    caption: undefined,
    delta: undefined,
    deltaCaption: undefined,
    sparkline: undefined,
    sparklineTone: 'brand',
    parts: undefined,
    restLabel: undefined,
    emphasis: false,
    icon: undefined,
    attention: false,
    hideLabel: false,
  },
)

const { t } = useI18n()
const { formatCompactNumber, formatDecimal } = useFormatters()

const counted = useCountUp(
  toRef(() => (props.displayOverride === undefined ? (props.value ?? 0) : 0)),
)

const display = computed(() => {
  if (props.displayOverride !== undefined) return props.displayOverride
  if (props.value === null) return null
  if (props.format === 'int') return formatCompactNumber(Math.round(counted.value))
  return formatDecimal(counted.value)
})

const hasSparkline = computed(() => (props.sparkline?.length ?? 0) >= 2)

const totalLabel = computed(() =>
  props.total === undefined ? undefined : formatCompactNumber(props.total),
)

const meterLabel = computed(() =>
  t('primitives.chart.fractionSr', {
    value: props.value ?? 0,
    total: props.total ?? 0,
    label: props.label,
  }),
)

defineSlots<{
  /** One small badge beside the value, e.g. "Page 1". */
  badge?(): unknown
  /** Extra visuals under the figure, after the gauge. */
  default?(): unknown
}>()
</script>

<template>
  <div class="flex min-w-0 flex-col gap-1">
    <span v-if="!hideLabel" class="text-body-sm text-text-secondary">{{ label }}</span>

    <div class="flex min-w-0 flex-wrap items-center gap-4">
      <p
        v-if="display === null"
        class="font-medium tabular-nums text-text-tertiary"
        :class="emphasis ? 'text-h1' : 'text-h2'"
        aria-hidden="true"
      >
        &ndash;&ndash;
      </p>
      <p
        v-else
        class="truncate font-medium tabular-nums text-text-primary"
        :class="emphasis ? 'text-h1' : 'text-h2'"
      >
        {{ display
        }}<template v-if="format === 'percent' && displayOverride === undefined">%</template
        ><span v-if="totalLabel !== undefined" class="ms-1 text-body text-text-tertiary"
          >/{{ totalLabel }}</span
        ><span v-if="suffix" class="ms-2 text-body text-text-tertiary">{{ suffix }}</span>
      </p>
      <slot name="badge" />
    </div>

    <span v-if="caption" class="text-caption tabular-nums text-text-tertiary">{{ caption }}</span>

    <div v-if="delta || hasSparkline" class="mt-4 flex items-end justify-between gap-2">
      <AppDeltaPill v-if="delta" :delta="delta" :caption="deltaCaption" />
      <AppSparkline
        v-if="hasSparkline"
        :values="sparkline ?? []"
        :tone="sparklineTone"
        :fill="!delta"
        :show-end-dot="emphasis"
      />
    </div>

    <AppMeter
      v-if="parts"
      class="mt-6"
      :label="meterLabel"
      :parts="parts"
      :total="total ?? 0"
      :rest-label="restLabel"
      legend="column"
    />
    <slot />
  </div>
</template>
